import React, { PropsWithChildren } from 'react';
import { gql } from '@yi/core';

import { greyishBlack } from '~/styles';
import { CardType } from '~/types';

import { AddToCalendarPromo } from './AddToCalendarMenu';
import { AddToPlaylistMenu } from './AddToPlayListMenu';
import { StyledMenuItem } from './elements';
import { FavoriteButton } from './FavoriteButton';
import { FullInfoButton } from './FullInfoButton';
import { RemoveFromPlaylist } from './RemoveFromPlaylist';
import { RemoveHistory } from './RemoveHistory';
import { UnregisterLiveButton } from './UnregisterLiveButton';
import { MenuProps, Menu } from '../Menu';

type LiveContent = gql.typesClient.Fragment_LiveCard['content'] & { registrant_id?: string | null };
type PracticeType = gql.typesClient.PracticeType;

interface Props extends Pick<MenuProps, 'iconButtonStyle' | 'iconStyle' | 'transformOrigin'> {
  showAddToPlaylist?: boolean;
  contentId: string;
  liveContent?: LiveContent;
  contentTitle?: string;
  removeButton?: { playlistId: string };
  removeHistory?: {
    item: { id: string; contentId: string; type: gql.typesClient.LogConsumptionTrackingType };
    isUserCreated?: boolean;
    onRemove?: (activityId: string) => void;
  };
  favoriteButton?: {
    isFavorite: boolean;
    isTeacher?: boolean;
    onRemove?: (contentId: string) => void;
  } | null;
  localizedPath?: string;
  onClick?(): void;
  cardType?: CardType;
  practiceType?: PracticeType;
  buttonLabel?: string;
  showAddToCalendarNewFeaturePopup?: boolean;
  playerMode?: boolean;
}

const WithNewFeaturePopup: React.FC<PropsWithChildren<{ showPopup: boolean }>> = ({
  showPopup,
  children,
}) => {
  return showPopup && React.isValidElement(children) ? (
    <AddToCalendarPromo>{children}</AddToCalendarPromo>
  ) : (
    <>{children}</>
  );
};

export const ContentCardMenu: React.FC<PropsWithChildren<Props>> = ({
  showAddToPlaylist = true,
  liveContent,
  contentId,
  contentTitle,
  removeButton,
  removeHistory,
  favoriteButton = { isFavorite: false },
  localizedPath,
  cardType,
  practiceType,
  showAddToCalendarNewFeaturePopup = false,
  playerMode = false,
  children,
  ...props
}) => {
  const getMenuItems: React.ComponentProps<typeof Menu>['getMenuItems'] = closeMenu => {
    const options = [];

    if (removeHistory) {
      options.push(
        <RemoveHistory
          key="RemoveHistory"
          item={removeHistory.item}
          onRemove={removeHistory.onRemove}
        />,
      );
      if (removeHistory.isUserCreated) return options;
    }

    if (liveContent) {
      options.push(
        <StyledMenuItem key="UnregisterLiveButton">
          <UnregisterLiveButton displayIcon contentId={contentId} liveContent={liveContent} />
        </StyledMenuItem>,
      );
    }

    if (favoriteButton && !removeButton && !liveContent)
      options.push(
        <StyledMenuItem key="FavoriteButton">
          <FavoriteButton
            displayIcon
            contentId={contentId}
            contentTitle={contentTitle}
            cardType={cardType}
            practiceType={practiceType}
            isFavorite={favoriteButton.isFavorite}
            isTeacher={favoriteButton.isTeacher}
            onRemove={favoriteButton.onRemove}
          />
        </StyledMenuItem>,
      );

    if (localizedPath)
      options.push(
        <StyledMenuItem key="FullInfo">
          <FullInfoButton
            localizedPath={localizedPath}
            contentTitle={contentTitle || ''}
            cardType={cardType}
          />
        </StyledMenuItem>,
      );

    if (removeButton)
      options.push(
        <RemoveFromPlaylist
          key="RemoveFromPlaylist"
          playlistId={removeButton.playlistId}
          contentId={contentId}
          callback={closeMenu}
        />,
      );

    if (showAddToPlaylist)
      options.push(
        <AddToPlaylistMenu
          key="AddToPlaylist"
          excludeIds={removeButton && [removeButton.playlistId]}
          contentId={contentId}
          contentTitle={contentTitle}
          cardType={cardType}
        />,
      );

    /**
     * current approach is made on the implicit set of menu items based on the input props.
     * For easier maintenance and clear menu items dependencies, "Compound Components" pattern should be used.
     * See 'AddToCalendarMenu' cmt as a reference
     * TODO refactor other menu items
     */
    const childrenArray = React.Children.toArray(children);
    options.push(...childrenArray);

    return options;
  };

  return (
    <WithNewFeaturePopup showPopup={showAddToCalendarNewFeaturePopup}>
      <Menu
        playerMode={playerMode}
        paperStyle={liveContent ? { width: 180 } : undefined}
        bodyTestId={`more-menu-${
          contentId !== '206' && contentId !== '718' ? contentTitle : contentId
        }`}
        iconStyle={{ color: greyishBlack, marginTop: '2px' }}
        iconTestId={`more-menu-icon`}
        getMenuItems={getMenuItems}
        transformOrigin={liveContent ? { vertical: 'top', horizontal: 'right' } : undefined}
        {...props}
      />
    </WithNewFeaturePopup>
  );
};
